// =============================================================================
// MeuDriver — useReferrals (Batch L4)
// Frontend hooks para o programa de indicação.
// O backend foi implementado no Batch C (20260521_referrals.sql).
// =============================================================================

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ReferralPerson {
  // ALTO-02: id é sintético (índice string) — a RPC não expõe UUIDs reais por privacidade
  id: string
  // full_name é o nome mascarado retornado pela RPC (ex: "João S.")
  full_name: string | null
  created_at: string
  /** true se o indicado se cadastrou há mais de 7 dias (proxy de atividade) */
  is_active: boolean
}

// ─── Keys ────────────────────────────────────────────────────────────────────

export const referralKeys = {
  myCode: (userId: string) => ['referral-code', userId] as const,
  myReferrals: (userId: string) => ['my-referrals', userId] as const,
  myCredits: (userId: string) => ['referral-credits', userId] as const,
}

// ─── useMyReferralCode ────────────────────────────────────────────────────────
// Retorna o código de referral do usuário autenticado (de profiles.referral_code).

export function useMyReferralCode() {
  const { user } = useAuth()

  return useQuery<string | null>({
    queryKey: referralKeys.myCode(user?.id ?? ''),
    enabled: Boolean(user?.id),
    staleTime: Infinity, // código não muda
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('referral_code')
        .eq('id', user!.id)
        .single()

      if (error) throw error
      return (data as any)?.referral_code ?? null
    },
  })
}

// ─── useMyReferralCredits ─────────────────────────────────────────────────────
// Saldo atual de créditos de referral do usuário.

export function useMyReferralCredits() {
  const { user } = useAuth()

  return useQuery<number>({
    queryKey: referralKeys.myCredits(user?.id ?? ''),
    enabled: Boolean(user?.id),
    staleTime: 1000 * 60 * 2,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('referral_credits')
        .eq('id', user!.id)
        .single()

      if (error) throw error
      return parseFloat(String((data as any)?.referral_credits ?? 0))
    },
  })
}

// ─── useMyReferrals ───────────────────────────────────────────────────────────
// Lista de pessoas que se cadastraram com o código do usuário.
// ALTO-02: substituída query direta em profiles (violava RLS) por RPC
// get_my_referrals (SECURITY DEFINER), que retorna apenas dados mínimos
// mascarados — sem acesso a PII de outros usuários via client.

export function useMyReferrals() {
  const { user } = useAuth()

  return useQuery<ReferralPerson[]>({
    queryKey: referralKeys.myReferrals(user?.id ?? ''),
    enabled: Boolean(user?.id),
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_my_referrals')

      if (error) throw error

      return ((data ?? []) as {
        referred_name: string
        joined_at: string
        status: string
      }[]).map((row, index) => ({
        // id sintético para uso como key em listas React — não é UUID real
        id: `referral-${index}`,
        full_name: row.referred_name,
        created_at: row.joined_at,
        is_active: row.status === 'active',
      }))
    },
  })
}

// ─── useReferralLink ──────────────────────────────────────────────────────────
// Retorna a URL completa com ?ref=CÓDIGO para compartilhamento.

export function useReferralLink() {
  const { data: code } = useMyReferralCode()
  if (!code) return null
  const base = typeof window !== 'undefined' ? window.location.origin : ''
  return `${base}/cadastro?ref=${code}`
}

// maskName removida — a RPC get_my_referrals retorna o nome já mascarado server-side.
